import React, { useState, useEffect } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { InputMask } from 'primereact/inputmask';
import { FaBuilding, FaUserTie, FaCalendarAlt, FaClock, FaTooth, FaUser, FaEnvelope, FaPhone, FaMapMarkerAlt } from 'react-icons/fa';

const Agendamento = () => {
    // Estados do fluxo
    const [empresaSelecionada, setEmpresaSelecionada] = useState(null);
    const [profissionalSelecionado, setProfissionalSelecionado] = useState(null);
    const [dataSelecionada, setDataSelecionada] = useState(null);
    const [horarioSelecionado, setHorarioSelecionado] = useState(null);
    const [servicoSelecionado, setServicoSelecionado] = useState(null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);
    const [carregandoDados, setCarregandoDados] = useState(true);

    // Estados para os dialogs de confirmação
    const [showSuccessDialog, setShowSuccessDialog] = useState(false);
    const [showErrorDialog, setShowErrorDialog] = useState(false);
    const [dialogMessage, setDialogMessage] = useState('');

    // Estados auxiliares
    const [mostrarModal, setMostrarModal] = useState(false);
    const [dadosPaciente, setDadosPaciente] = useState({
        Nome: '',
        cpf: '',
        email: '',
        telefone: ''
    });
    const [mesAtual, setMesAtual] = useState(new Date().getMonth());
    const [anoAtual, setAnoAtual] = useState(new Date().getFullYear());

    // Dados dinâmicos do banco - ESTRUTURA ATUALIZADA
    const [dadosCompletos, setDadosCompletos] = useState({
        empresas: [],
        profissionais: [],
        servicos: [],
        datas_atendimento: [],
        horarios: [],
    });

    // Função para buscar todos os dados
    const fetchTodosDados = async () => {
        setCarregandoDados(true);
        setError(null);

        try {
            const response = await fetch(
                'https://helder.cantarellisolucoes.com.br/wp-json/custom/v1/agendamento/dados',
                {
                    method: 'GET',
                    headers: {
                        'Accept': 'application/json'
                    }
                }
            );

            if (!response.ok) throw new Error(`HTTP ${response.status} - ${response.statusText}`);

            const result = await response.json();

            if (result.success && result.data) {
                setDadosCompletos({
                    empresas: result.data.empresas || [],
                    profissionais: result.data.profissionais || [],
                    servicos: result.data.servicos || [],
                    datas_atendimento: result.data.datas_atendimento || [],
                    horarios: result.data.horarios || [],
                    agendamentos_existentes: result.data.agendamentos_existentes || []
                });
            } else {
                throw new Error(result.message || "Erro na estrutura dos dados");
            }

        } catch (error) {
            console.error('❌ Erro ao carregar dados:', error);
            setError(error.message);
            setDadosCompletos({
                empresas: [],
                profissionais: [],
                servicos: [],
                datas_atendimento: [],
                horarios: [],
                agendamentos_existentes: []
            })
        } finally {
            setCarregandoDados(false);
        }
    };

    // Carregar dados quando o componente montar
    useEffect(() => {
        fetchTodosDados();
    }, []);

    // Filtros
    const profissionaisDisponiveis = empresaSelecionada && dadosCompletos.profissionais
        ? dadosCompletos.profissionais.filter(prof =>
            prof.empresas && prof.empresas.some(emp => emp.id == empresaSelecionada.id)
        )
        : [];

    // 🔥 CORREÇÃO: Filtrar serviços pelo profissional selecionado
    const servicosDisponiveis = profissionalSelecionado && dadosCompletos.servicos
        ? dadosCompletos.servicos.filter(servico => {
            // Verificar se o profissional tem este serviço em sua lista de serviços
            const profissionalTemServico = profissionalSelecionado.servicos &&
                profissionalSelecionado.servicos.some(serv => serv.id == servico.id);

            return profissionalTemServico;
        })
        : [];

    // VERIFICAÇÃO DE DATA DISPONÍVEL - ATUALIZADA
    const isDataDentroDoLimite = (data) => {
        const hoje = new Date();
        hoje.setHours(0, 0, 0, 0);

        const limite = new Date(hoje);
        limite.setDate(limite.getDate() + 90);

        return data <= limite;
    };

    const isDataDisponivel = (data) => {
        if (!profissionalSelecionado || !data || !dadosCompletos.datas_atendimento) return false;

        if (!isDataDentroDoLimite(data)) return false;

        const dataString = data.toISOString().split('T')[0];
        return dadosCompletos.datas_atendimento.some(dataAtend =>
            dataAtend.profissional_id == profissionalSelecionado.id &&
            dataAtend.data_atendimento === dataString
        );
    };

    // FUNÇÃO PARA VERIFICAR SE HORÁRIO ESTÁ OCUPADO
    const isHorarioOcupado = (data, hora) => {
        if (!data || !hora || !dadosCompletos.agendamentos_existentes) {
            return false;
        }

        const dataString = data.toISOString().split('T')[0];
        const horaFormatada = hora;

        const ocupado = dadosCompletos.agendamentos_existentes.some(agendamento => {
            const matchProfissional = agendamento.profissional_id == profissionalSelecionado.id;
            const matchData = agendamento.data_atendimento === dataString;
            const matchHora = agendamento.horario === horaFormatada;
            const matchCompleto = matchProfissional && matchData && matchHora;

            return matchCompleto;
        });

        return ocupado;
    };

    // FUNÇÃO ATUALIZADA PARA OBTER TODOS OS HORÁRIOS (COM STATUS)
    const getTodosHorariosComStatus = () => {
        if (!dataSelecionada || !profissionalSelecionado) return [];

        const dataFormatada = dataSelecionada.toISOString().split('T')[0];
        const diaSemana = dataSelecionada.getDay();

        const todosHorarios = dadosCompletos.horarios
            ?.filter(h =>
                h.profissional_id == profissionalSelecionado.id &&
                h.dia_semana == diaSemana
            )
            .map(h => {
                const horarioPassou = isHorarioPassado(dataSelecionada, h.hora);
                const horarioOcupado = isHorarioOcupado(dataSelecionada, h.hora);
                const disponivel = !horarioPassou && !horarioOcupado;

                return {
                    hora: h.hora,
                    id: `horario-${h.hora}`,
                    disponivel: disponivel,
                    ocupado: horarioOcupado,
                    passou: horarioPassou
                };
            }) || [];

        return todosHorarios;
    };

    // FUNÇÃO PARA VERIFICAR SE O HORÁRIO JÁ PASSOU
    const isHorarioPassado = (data, hora) => {
        const agora = new Date();
        const hoje = new Date(agora.getFullYear(), agora.getMonth(), agora.getDate());

        if (data.toDateString() === hoje.toDateString()) {
            const [horas, minutos] = hora.split(':').map(Number);
            const horarioSelecionado = new Date();
            horarioSelecionado.setHours(horas, minutos, 0, 0);

            return horarioSelecionado < agora;
        }

        if (data < hoje) {
            return true;
        }

        return false;
    };

    // Use isso no seu estado - ATUALIZADO
    const [horariosDisponiveis, setHorariosDisponiveis] = useState([]);

    // Atualize os horários quando a data ou profissional mudar - ATUALIZADO
    useEffect(() => {
        if (dataSelecionada && profissionalSelecionado) {
            const horarios = getTodosHorariosComStatus();
            setHorariosDisponiveis(horarios);
        } else {
            setHorariosDisponiveis([]);
        }
    }, [dataSelecionada, profissionalSelecionado, dadosCompletos]);

    // Resto do código permanece igual...
    const gerarDiasDoMes = () => {
        const primeiroDia = new Date(anoAtual, mesAtual, 1);
        const ultimoDia = new Date(anoAtual, mesAtual + 1, 0);

        const dias = [];
        for (let i = 0; i < primeiroDia.getDay(); i++) {
            dias.push(null);
        }
        for (let i = 1; i <= ultimoDia.getDate(); i++) {
            dias.push(new Date(anoAtual, mesAtual, i));
        }
        return dias;
    };

    const dias = gerarDiasDoMes();

    const mudarMes = (incremento) => {
        const novoMes = mesAtual + incremento;
        const dataTeste = new Date(anoAtual, novoMes, 1);

        const hoje = new Date();
        const limite = new Date();
        limite.setMonth(hoje.getMonth() + 3);
        limite.setDate(1);

        if (dataTeste > limite) {
            console.log('Navegação bloqueada: limite de 90 dias atingido');
            return;
        }

        if (novoMes < 0) {
            setMesAtual(11);
            setAnoAtual(anoAtual - 1);
        } else if (novoMes > 11) {
            setMesAtual(0);
            setAnoAtual(anoAtual + 1);
        } else {
            setMesAtual(novoMes);
        }
    };

    const nomesMeses = [
        'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
        'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
    ];

    const formatarTempo = (duracao) => {
        if (!duracao) return 'A combinar';
        return `${duracao} minutos`;
    };

    const formatarLocalizacao = (empresa) => {
        return `${empresa.cidade} - ${empresa.estado}`;
    };

    // Handlers
    const handleSelecionarEmpresa = (empresa) => {
        setEmpresaSelecionada(empresa);
        setProfissionalSelecionado(null);
        setDataSelecionada(null);
        setHorarioSelecionado(null);
        setServicoSelecionado(null);
    };

    const handleSelecionarProfissional = (profissional) => {
        setProfissionalSelecionado(profissional);
        setDataSelecionada(null);
        setHorarioSelecionado(null);
        setServicoSelecionado(null);
    };

    const handleSelecionarData = (data) => {
        if (data && isDataDisponivel(data)) {
            setDataSelecionada(new Date(data.getFullYear(), data.getMonth(), data.getDate()));
            setHorarioSelecionado(null);
            setServicoSelecionado(null);
        }
    };

    const handleAgendar = () => {
        if (empresaSelecionada && profissionalSelecionado && dataSelecionada && horarioSelecionado && servicoSelecionado) {
            if (isHorarioPassado(dataSelecionada, horarioSelecionado.hora)) {
                showErrorMessage('Este horário não está mais disponível. Por favor, selecione outro horário.');
                return;
            }

            if (isHorarioOcupado(dataSelecionada, horarioSelecionado.hora)) {
                showErrorMessage('Este horário já foi reservado. Por favor, selecione outro horário.');
                return;
            }

            setMostrarModal(true);
        } else {
            showErrorMessage('Por favor, complete todas as etapas antes de agendar.');
        }
    };

    const handleConfirmarAgendamento = async () => {
        if (!dadosPaciente.cpf) {
            showErrorMessage('CPF é obrigatório para o agendamento');
            return;
        }

        if (!dadosPaciente.Nome) {
            showErrorMessage('Nome é obrigatório para o agendamento');
            return;
        }

        setLoading(true);

        try {
            const agendamentoData = {
                empresa_id: empresaSelecionada.id,
                profissional_id: profissionalSelecionado.id,
                servico_id: servicoSelecionado.id,
                data_hora: `${dataSelecionada.toISOString().split('T')[0]} ${horarioSelecionado.hora}:00`,
                paciente: {
                    nome: dadosPaciente.Nome,
                    cpf: dadosPaciente.cpf,
                    telefone: dadosPaciente.telefone,
                    email: dadosPaciente.email
                }
            };

            console.log('📤 Enviando agendamento:', agendamentoData);

            const response = await fetch('https://helder.cantarellisolucoes.com.br/wp-json/custom/v1/agenda', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                },
                body: JSON.stringify(agendamentoData)
            });

            if (!response.ok) {
                let errorMessage = "Erro ao realizar agendamento";
                try {
                    const errorResult = await response.json();
                    errorMessage = errorResult.message || errorMessage;
                } catch (e) {
                    const errorText = await response.text();
                    errorMessage = errorText || errorMessage;
                }
                throw new Error(errorMessage);
            }

            const result = await response.json();
            console.log('✅ Agendamento realizado:', result);

            // Resetar formulário
            setEmpresaSelecionada(null);
            setProfissionalSelecionado(null);
            setDataSelecionada(null);
            setHorarioSelecionado(null);
            setServicoSelecionado(null);
            setDadosPaciente({
                Nome: '',
                cpf: '',
                email: '',
                telefone: ''
            });
            setMostrarModal(false);

            // Recarregar dados para atualizar agendamentos
            await fetchTodosDados();

            showSuccessMessage('Agendamento realizado com sucesso!');

        } catch (error) {
            console.error('❌ Erro ao agendar:', error);
            showErrorMessage(error.message);
        } finally {
            setLoading(false);
        }
    };

    // Função para mostrar mensagem de sucesso
    const showSuccessMessage = (message) => {
        setDialogMessage(message);
        setShowSuccessDialog(true);
    };

    // Função para mostrar mensagem de erro
    const showErrorMessage = (message) => {
        setDialogMessage(message);
        setShowErrorDialog(true);
    };

    return (
        <div style={{ fontFamily: "'Rajdhani', sans-serif", minHeight: "100vh", padding: "12px" }}>
            {error && (
                <div style={{
                    padding: '10px',
                    backgroundColor: '#fff3cd',
                    border: '1px solid #ffeaa7',
                    borderRadius: '6px',
                    marginBottom: '12px',
                    color: '#856404',
                    textAlign: 'center',
                    fontSize: '0.9rem'
                }}>
                    ⚠️ {error} - Usando dados de exemplo para demonstração
                </div>
            )}

            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>

                {/* Dialog de Sucesso */}
                <Dialog
                    visible={showSuccessDialog}
                    style={{ width: '320px', fontFamily: "'Rajdhani', sans-serif" }}
                    onHide={() => setShowSuccessDialog(false)}
                    draggable={false}
                    closable={false}
                    header={null}
                >
                    <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: '15px',
                        padding: '8px',
                    }}>
                        <div style={{
                            color: '#22C55E',
                            fontSize: '2rem'
                        }}>
                            <i className="pi pi-check-circle" style={{ fontSize: '2.2rem' }}></i>
                        </div>
                        <p style={{
                            textAlign: 'center',
                            fontSize: '0.95rem',
                            margin: 0,
                            color: '#374151',
                        }}>
                            {dialogMessage}
                        </p>
                        <Button
                            label="OK"
                            icon="pi pi-check"
                            onClick={() => setShowSuccessDialog(false)}
                            className="p-button-success"
                            style={{
                                padding: '5px 18px',
                                fontWeight: 'bold',
                                fontSize: '0.85rem'
                            }}
                        />
                    </div>
                </Dialog>

                {/* Dialog de Erro */}
                <Dialog
                    visible={showErrorDialog}
                    style={{ width: '320px', fontFamily: "'Rajdhani', sans-serif" }}
                    onHide={() => setShowErrorDialog(false)}
                    draggable={false}
                    closable={false}
                    header={null}
                >
                    <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: '15px',
                        padding: '8px',
                    }}>
                        <div style={{
                            color: '#EF4444',
                            fontSize: '2rem'
                        }}>
                            <i className="pi pi-times-circle" style={{ fontSize: '2.2rem' }}></i>
                        </div>
                        <p style={{
                            textAlign: 'center',
                            fontSize: '0.95rem',
                            margin: 0,
                            color: '#374151',
                        }}>
                            {dialogMessage}
                        </p>
                        <Button
                            label="OK"
                            onClick={() => setShowErrorDialog(false)}
                            className="p-button-danger"
                            style={{
                                padding: '5px 18px',
                                fontWeight: 'bold',
                                fontSize: '0.85rem'
                            }}
                        />
                    </div>
                </Dialog>

                {/* Modal de Confirmação */}
                <Dialog
                    header="Informações do Paciente"
                    visible={mostrarModal}
                    style={{
                        width: '90vw',
                        maxWidth: '600px',
                        fontFamily: "'Rajdhani', sans-serif"
                    }}
                    onHide={() => !loading && setMostrarModal(false)}
                >
                    <div style={{ display: 'grid', gap: '15px' }}>
                        <div style={{ display: "flex", gap: "10px", width: "100%" }}>
                            <div style={{ display: "flex", flexDirection: "column", flex: 1 }}>
                                <div style={{ display: "flex", alignItems: "center", gap: "5px", marginBottom: '3px' }}>
                                    <FaUser style={{ color: '#CE3D38', fontSize: '0.9rem' }} />
                                    <div style={{ fontSize: '0.9rem' }}>Nome:</div>
                                </div>
                                <InputText
                                    value={dadosPaciente.Nome}
                                    onChange={(e) => setDadosPaciente({ ...dadosPaciente, Nome: e.target.value })}
                                    style={{ width: '100%', height: '32px', fontFamily: "'Rajdhani', sans-serif", fontSize: '0.9rem' }}
                                />
                            </div>
                            <div style={{ display: "flex", flexDirection: "column", flex: 1 }}>
                                <div style={{ display: "flex", alignItems: "center", gap: "5px", marginBottom: '3px' }}>
                                    <FaUser style={{ color: '#CE3D38', fontSize: '0.9rem' }} />
                                    <div style={{ fontSize: '0.9rem' }}>CPF:</div>
                                </div>
                                <InputMask
                                    value={dadosPaciente.cpf}
                                    mask="999.999.999-99"
                                    onChange={(e) => setDadosPaciente({ ...dadosPaciente, cpf: e.target.value })}
                                    style={{ width: '100%', height: '32px', fontFamily: "'Rajdhani', sans-serif", fontSize: '0.9rem' }}
                                />
                            </div>
                        </div>

                        <div style={{ display: "flex", gap: "10px", width: "100%" }}>
                            <div style={{ display: "flex", flexDirection: "column", flex: 1 }}>
                                <div style={{ display: "flex", alignItems: "center", gap: "5px", marginBottom: '3px' }}>
                                    <FaEnvelope style={{ color: '#CE3D38', fontSize: '0.9rem' }} />
                                    <div style={{ fontSize: '0.9rem' }}>Email:</div>
                                </div>
                                <InputText
                                    value={dadosPaciente.email}
                                    onChange={(e) => setDadosPaciente({ ...dadosPaciente, email: e.target.value })}
                                    style={{ width: '100%', height: '32px', fontFamily: "'Rajdhani', sans-serif", fontSize: '0.9rem' }}
                                />
                            </div>
                            <div style={{ display: "flex", flexDirection: "column", flex: 1 }}>
                                <div style={{ display: "flex", alignItems: "center", gap: "5px", marginBottom: '3px' }}>
                                    <FaPhone style={{ color: '#CE3D38', fontSize: '0.9rem' }} />
                                    <div style={{ fontSize: '0.9rem' }}>Telefone:</div>
                                </div>
                                <InputMask
                                    value={dadosPaciente.telefone}
                                    mask="(99) 99999-9999"
                                    onChange={(e) => setDadosPaciente({ ...dadosPaciente, telefone: e.target.value })}
                                    style={{ width: '100%', height: '32px', fontFamily: "'Rajdhani', sans-serif", fontSize: '0.9rem' }}
                                />
                            </div>
                        </div>

                        {/* Resumo do Agendamento */}
                        <div style={{
                            backgroundColor: '#F8F9FA',
                            padding: '15px',
                            borderRadius: '6px',
                            border: '1px solid #e0e0e0',
                            marginTop: '5px'
                        }}>
                            <h3 style={{ margin: '0 0 8px 0', color: '#CE3D38', fontSize: '1rem' }}>Resumo do Agendamento</h3>
                            <div style={{ display: 'grid', gap: '4px', fontSize: '0.85rem' }}>
                                <p style={{ margin: '0' }}><strong>Cidade:</strong> {empresaSelecionada ? formatarLocalizacao(empresaSelecionada) : 'Não selecionada'}</p>
                                <p style={{ margin: '0' }}><strong>Profissional:</strong> {profissionalSelecionado?.nome || 'Não selecionado'}</p>
                                <p style={{ margin: '0' }}><strong>Especialidade:</strong> {profissionalSelecionado?.especialidade || ''}</p>
                                <p style={{ margin: '0' }}><strong>Data:</strong> {dataSelecionada?.toLocaleDateString('pt-BR') || 'Não selecionada'}</p>
                                <p style={{ margin: '0' }}><strong>Horário:</strong> {horarioSelecionado?.hora || 'Não selecionado'}</p>
                                <p style={{ margin: '0' }}><strong>Serviço:</strong> {servicoSelecionado?.nome || 'Não selecionado'}</p>
                            </div>
                        </div>

                        {/* Botões de ação */}
                        <div style={{ display: "flex", justifyContent: "center", gap: "8px", marginTop: '10px' }}>
                            <Button
                                label="Cancelar"
                                icon="pi pi-times"
                                onClick={() => setMostrarModal(false)}
                                className="p-button-danger"
                                style={{ padding: "6px 14px", fontSize: '0.85rem' }}
                                disabled={loading}
                            />
                            <Button
                                label={loading ? "Agendando..." : "Confirmar"}
                                icon="pi pi-check"
                                onClick={handleConfirmarAgendamento}
                                className="p-button-success"
                                style={{ padding: "6px 14px", fontSize: '0.85rem' }}
                                disabled={loading}
                                loading={loading}
                            />
                        </div>
                    </div>
                </Dialog>

                {/* Passo 1: Seleção da Empresa/Cidade */}
                <div
                    className="p-2 shadow-2"
                    style={{
                        backgroundColor: "#fff",
                        borderRadius: '8px',
                        transition: "0.3s ease",
                    }}
                >
                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
                        <FaBuilding style={{ marginRight: '8px', color: '#CE3D38', fontSize: '1.1rem' }} />
                        <h2 style={{ margin: 0, fontSize: '1.2rem' }}>Selecione a Cidade</h2>
                    </div>

                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                        gap: '10px'
                    }}>
                        {dadosCompletos.empresas && dadosCompletos.empresas.map((empresa) => (
                            <div
                                key={empresa.id}
                                onClick={() => handleSelecionarEmpresa(empresa)}
                                style={{
                                    padding: '10px 10px 10px 10px',
                                    borderRadius: '8px',
                                    backgroundColor: empresaSelecionada?.id === empresa.id ? '#FFF0F0' : '#F8F9FA',
                                    border: empresaSelecionada?.id === empresa.id ? '2px solid #CE3D38' : '1px solid #e0e0e0',
                                    cursor: 'pointer',
                                    transition: 'all 0.3s ease',
                                }}
                            >
                                <div style={{ display: 'flex', alignItems: 'center' }}>
                                    <FaMapMarkerAlt style={{ marginRight: '8px', color: '#CE3D38', fontSize: '0.9rem' }} />
                                    <h3 style={{ margin: 0, fontSize: '1rem' }}>{empresa.nome}</h3>
                                </div>
                                <p style={{ marginLeft: '23px', margin: '2px 3px 3px 23px', color: '#666', fontSize: '0.85rem' }}>{formatarLocalizacao(empresa)}</p>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Passo 2: Seleção do Profissional */}
                {empresaSelecionada && (
                    <div
                        className="p-2 shadow-2"
                        style={{
                            backgroundColor: "#fff",
                            borderRadius: '8px',
                            transition: "0.3s ease",
                        }}
                    >
                        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
                            <FaUserTie style={{ marginRight: '8px', color: '#CE3D38', fontSize: '1.1rem' }} />
                            <h2 style={{ margin: 0, fontSize: '1.2rem' }}>Profissionais Disponíveis</h2>
                        </div>

                        {profissionaisDisponiveis && profissionaisDisponiveis.length > 0 ? (
                            <div style={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                                gap: '10px'
                            }}>
                                {profissionaisDisponiveis.map((prof) => (
                                    <div
                                        key={prof.id}
                                        onClick={() => handleSelecionarProfissional(prof)}
                                        style={{
                                            padding: '10px 10px 5px 10px',
                                            borderRadius: '8px',
                                            backgroundColor: profissionalSelecionado?.id === prof.id ? '#FFF0F0' : '#F8F9FA',
                                            border: profissionalSelecionado?.id === prof.id ? '2px solid #CE3D38' : '1px solid #e0e0e0',
                                            cursor: 'pointer',
                                            transition: 'all 0.3s ease',
                                        }}
                                    >
                                        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                                            <div style={{
                                                width: '38px',
                                                height: '38px',
                                                borderRadius: '50%',
                                                backgroundColor: '#FFF0F0',
                                                display: 'flex',
                                                justifyContent: 'center',
                                                alignItems: 'center',
                                                marginRight: '8px'
                                            }}>
                                                <FaUserTie size={20} color="#CE3D38" />
                                            </div>
                                            <div>
                                                <h3 style={{ margin: 0, fontSize: '1rem' }}>{prof.nome}</h3>
                                                <p style={{ margin: 0, color: '#666', fontSize: '0.85rem' }}>{prof.especialidade}</p>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div style={{
                                textAlign: 'center',
                                padding: '25px',
                                color: '#6c757d',
                                fontStyle: 'italic',
                                fontSize: '0.9rem'
                            }}>
                                Nenhum profissional disponível para esta cidade.
                            </div>
                        )}
                    </div>
                )}

                {/* Passo 3: Seleção da Data */}
                {profissionalSelecionado && (
                    <div
                        className="p-2 shadow-2"
                        style={{
                            backgroundColor: "#fff",
                            borderRadius: '8px',
                            transition: "0.3s ease",

                        }}
                    >
                        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
                            <FaCalendarAlt style={{ marginRight: '8px', color: '#CE3D38', fontSize: '1.1rem' }} />
                            <h2 style={{ margin: 0, fontSize: '1.2rem' }}>Selecione a Data</h2>
                            <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <span style={{ fontSize: '0.85rem', color: '#666' }}>
                                    {profissionalSelecionado.nome} - {profissionalSelecionado.especialidade}
                                </span>
                            </div>
                        </div>

                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                            <Button
                                icon="pi pi-angle-left"
                                onClick={() => mudarMes(-1)}
                                className="p-button-text"
                                style={{ color: '#CE3D38', padding: '4px' }}
                            />
                            <div style={{ fontWeight: 'bold', fontSize: '1.1rem', color: '#CE3D38' }}>
                                {nomesMeses[mesAtual]} {anoAtual}
                            </div>
                            <Button
                                icon="pi pi-angle-right"
                                onClick={() => mudarMes(1)}
                                className="p-button-text"
                                style={{ color: '#CE3D38', padding: '4px' }}
                            />
                        </div>

                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(7, 1fr)',
                            gap: '4px'
                        }}>
                            {['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'].map(dia => (
                                <div key={dia} style={{
                                    textAlign: 'center',
                                    fontWeight: 'bold',
                                    color: '#CE3D38',
                                    fontSize: '0.85rem',
                                    padding: '4px 0'
                                }}>
                                    {dia}
                                </div>
                            ))}

                            {dias.map((dia, index) => {
                                const dentroDoLimite = dia ? isDataDentroDoLimite(dia) : false;
                                const disponivel = dentroDoLimite && isDataDisponivel(dia);

                                return (
                                    <div
                                        key={index}
                                        onClick={() => disponivel && handleSelecionarData(dia)}
                                        style={{
                                            height: '36px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            borderRadius: '4px',
                                            backgroundColor: dia && dataSelecionada && dia.toDateString() === dataSelecionada.toDateString()
                                                ? '#FFF0F0'
                                                : disponivel ? '#F8F9FA' : '#f5f5f5',
                                            border: dia && dataSelecionada && dia.toDateString() === dataSelecionada.toDateString()
                                                ? '2px solid #CE3D38'
                                                : disponivel ? '1px solid #e0e0e0' : '1px solid #f0f0f0',
                                            cursor: disponivel ? 'pointer' : 'not-allowed',
                                            opacity: dia ? 1 : 0,
                                            color: disponivel ? '#333' : '#ccc',
                                            fontSize: '0.9rem',
                                            position: 'relative'
                                        }}
                                        title={!dentroDoLimite ? 'Data além do período de agendamento (90 dias)' :
                                            disponivel ? 'Data disponível' : 'Data indisponível'}
                                    >
                                        {dia && dia.getDate()}
                                        {/* Bolinha VERDE para disponível */}
                                        {disponivel && dia && (
                                            <div style={{
                                                position: 'absolute',
                                                top: '2px',
                                                right: '2px',
                                                width: '5px',
                                                height: '5px',
                                                backgroundColor: '#28a745',
                                                borderRadius: '50%'
                                            }} />
                                        )}
                                        {/* Bolinha VERMELHA para indisponível ou fora do limite */}
                                        {(!disponivel || !dentroDoLimite) && dia && (
                                            <div style={{
                                                position: 'absolute',
                                                top: '2px',
                                                right: '2px',
                                                width: '5px',
                                                height: '5px',
                                                backgroundColor: '#ff4444',
                                                borderRadius: '50%'
                                            }} />
                                        )}
                                    </div>
                                );
                            })}
                        </div>

                        {/* Legenda ATUALIZADA */}
                        <div style={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            marginTop: '12px',
                            gap: '12px',
                            fontSize: '0.75rem',
                            color: '#666',
                            flexWrap: 'wrap'
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                <div style={{
                                    width: '10px',
                                    height: '10px',
                                    backgroundColor: '#FFF0F0',
                                    border: '1px solid #CE3D38',
                                    borderRadius: '2px'
                                }}></div>
                                <span>Selecionada</span>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                <div style={{
                                    width: '10px',
                                    height: '10px',
                                    backgroundColor: '#F8F9FA',
                                    border: '1px solid #e0e0e0',
                                    borderRadius: '2px'
                                }}></div>
                                <div style={{
                                    width: '5px',
                                    height: '5px',
                                    backgroundColor: '#28a745',
                                    borderRadius: '50%'
                                }}></div>
                                <span>Disponível</span>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                <div style={{
                                    width: '10px',
                                    height: '10px',
                                    backgroundColor: '#f5f5f5',
                                    border: '1px solid #f0f0f0',
                                    borderRadius: '2px'
                                }}></div>
                                <div style={{
                                    width: '5px',
                                    height: '5px',
                                    backgroundColor: '#ff4444',
                                    borderRadius: '50%'
                                }}></div>
                                <span>Indisponível</span>
                            </div>
                        </div>
                    </div>
                )}

                {/* Passo 4: Seleção do Horário */}
                {dataSelecionada && (
                    <div
                        className="p-2 shadow-2"
                        style={{
                            backgroundColor: "#fff",
                            borderRadius: '8px',
                            transition: "0.3s ease",
                        }}
                    >
                        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
                            <FaClock style={{ marginRight: '8px', color: '#CE3D38', fontSize: '1.1rem' }} />
                            <h2 style={{ margin: 0, fontSize: '1.2rem' }}>Horários Disponíveis</h2>
                            <div style={{ marginLeft: 'auto', fontSize: '0.85rem', color: '#666' }}>
                                {dataSelecionada.toLocaleDateString('pt-BR')}
                            </div>
                        </div>

                        {horariosDisponiveis.length > 0 ? (
                            <div style={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(auto-fill, minmax(75px, 1fr))',
                                gap: '8px'
                            }}>
                                {horariosDisponiveis.map((horarioObj) => {
                                    const estaDisponivel = horarioObj.disponivel;
                                    const estaOcupado = horarioObj.ocupado;
                                    const estaPassou = horarioObj.passou;
                                    const estaSelecionado = horarioSelecionado?.hora === horarioObj.hora;

                                    return (
                                        <div
                                            key={horarioObj.id}
                                            onClick={() => {
                                                if (estaDisponivel) {
                                                    setHorarioSelecionado(horarioObj);
                                                    setServicoSelecionado(null);
                                                }
                                            }}
                                            style={{
                                                padding: '8px',
                                                borderRadius: '6px',
                                                backgroundColor: estaSelecionado ? '#FFF0F0' :
                                                    estaDisponivel ? '#F8F9FA' : '#f8f9fa',
                                                border: estaSelecionado ? '2px solid #CE3D38' :
                                                    estaDisponivel ? '1px solid #e0e0e0' : '1px solid #d6d8db',
                                                cursor: estaDisponivel ? 'pointer' : 'not-allowed',
                                                transition: 'all 0.3s ease',
                                                textAlign: 'center',
                                                fontWeight: 'bold',
                                                position: 'relative',
                                                opacity: estaDisponivel ? 1 : 0.5,
                                                color: estaDisponivel ? '#333' : '#6c757d',
                                                fontSize: '0.9rem'
                                            }}
                                            title={estaPassou ? 'Horário já passou' :
                                                estaOcupado ? 'Horário já reservado' :
                                                    'Horário disponível'}
                                        >
                                            {horarioObj.hora}

                                            {/* Bolinha VERDE para disponível */}
                                            {estaDisponivel && (
                                                <div style={{
                                                    position: 'absolute',
                                                    top: '2px',
                                                    right: '2px',
                                                    width: '5px',
                                                    height: '5px',
                                                    backgroundColor: '#28a745',
                                                    borderRadius: '50%'
                                                }} />
                                            )}

                                            {/* Bolinha AMARELA para horário ocupado */}
                                            {estaOcupado && (
                                                <div style={{
                                                    position: 'absolute',
                                                    top: '2px',
                                                    right: '2px',
                                                    width: '5px',
                                                    height: '5px',
                                                    backgroundColor: '#ffc107',
                                                    borderRadius: '50%'
                                                }} />
                                            )}

                                            {/* Bolinha VERMELHA para horário que passou */}
                                            {estaPassou && (
                                                <div style={{
                                                    position: 'absolute',
                                                    top: '2px',
                                                    right: '2px',
                                                    width: '5px',
                                                    height: '5px',
                                                    backgroundColor: '#ff4444',
                                                    borderRadius: '50%'
                                                }} />
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        ) : (
                            <div style={{
                                textAlign: 'center',
                                padding: '25px',
                                color: '#6c757d',
                                fontStyle: 'italic',
                                fontSize: '0.9rem'
                            }}>
                                Nenhum horário disponível para esta data.
                            </div>
                        )}

                        {/* Legenda ATUALIZADA para horários */}
                        <div style={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            marginTop: '12px',
                            gap: '8px',
                            fontSize: '0.7rem',
                            color: '#666',
                            flexWrap: 'wrap'
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                <div style={{
                                    width: '5px',
                                    height: '5px',
                                    backgroundColor: '#28a745',
                                    borderRadius: '50%'
                                }}></div>
                                <span>Disponível</span>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                <div style={{
                                    width: '5px',
                                    height: '5px',
                                    backgroundColor: '#ffc107',
                                    borderRadius: '50%'
                                }}></div>
                                <span>Ocupado</span>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                <div style={{
                                    width: '5px',
                                    height: '5px',
                                    backgroundColor: '#ff4444',
                                    borderRadius: '50%'
                                }}></div>
                                <span>Indisponível</span>
                            </div>
                        </div>
                    </div>
                )}

                {/* Passo 5: Seleção do Serviço */}
                {horarioSelecionado && (
                    <div
                        className="p-2 shadow-2"
                        style={{
                            backgroundColor: "#fff",
                            borderRadius: '8px',
                            transition: "0.3s ease",
                        }}
                    >
                        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
                            <FaTooth style={{ marginRight: '8px', color: '#CE3D38', fontSize: '1.1rem' }} />
                            <h2 style={{ margin: 0, fontSize: '1.2rem' }}>Serviços Disponíveis</h2>
                        </div>

                        {servicosDisponiveis.length > 0 ? (
                            <div style={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                                gap: '10px'
                            }}>
                                {servicosDisponiveis.map((serv) => (
                                    <div
                                        key={serv.id}
                                        onClick={() => setServicoSelecionado(serv)}
                                        style={{
                                            padding: '10px',
                                            borderRadius: '8px',
                                            backgroundColor: servicoSelecionado?.id === serv.id ? '#FFF0F0' : '#F8F9FA',
                                            border: servicoSelecionado?.id === serv.id ? '2px solid #CE3D38' : '1px solid #e0e0e0',
                                            cursor: 'pointer',
                                            transition: 'all 0.3s ease',
                                        }}
                                    >
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <h3
                                                style={{
                                                    margin: 0,
                                                    fontSize: '0.95rem',
                                                    lineHeight: '1.2',
                                                    flex: 1,
                                                    wordBreak: 'break-word',
                                                    display: '-webkit-box',
                                                    WebkitLineClamp: 2,
                                                    WebkitBoxOrient: 'vertical',
                                                    overflow: 'hidden',
                                                    minHeight: '2.4em'
                                                }}
                                            >{serv.nome}</h3>
                                            <div style={{
                                                backgroundColor: '#FFF0F0',
                                                borderRadius: '50%',
                                                width: '32px',
                                                height: '32px',
                                                minWidth: '32px',
                                                display: 'flex',
                                                justifyContent: 'center',
                                                alignItems: 'center'
                                            }}>
                                                <FaTooth size={14} color="#CE3D38" />
                                            </div>
                                        </div>

                                        <div style={{ display: 'flex', alignItems: 'center' }}>
                                            <FaClock style={{ marginRight: '8px', color: '#666', fontSize: '0.9rem' }} />
                                            <span style={{ color: '#666', fontSize: '0.85rem' }}>
                                                {formatarTempo(serv.duracao)}
                                            </span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div style={{
                                textAlign: 'center',
                                padding: '25px',
                                color: '#6c757d',
                                fontStyle: 'italic',
                                fontSize: '0.9rem'
                            }}>
                                {profissionalSelecionado ?
                                    `Nenhum serviço disponível para ${profissionalSelecionado.nome}.`
                                    : 'Nenhum serviço disponível.'
                                }
                            </div>
                        )}
                    </div>
                )}

                {/* Botão Agendar */}
                {servicoSelecionado && (
                    <div style={{ textAlign: 'center', marginTop: '15px' }}>
                        <Button
                            label="Agendar Consulta"
                            icon="pi pi-calendar-plus"
                            onClick={handleAgendar}
                            style={{
                                backgroundColor: '#CE3D38',
                                border: 'none',
                                padding: '10px 25px',
                                fontSize: '1rem',
                                fontWeight: 'bold',
                                borderRadius: '6px'
                            }}
                        />
                    </div>
                )}

            </div >
        </div >
    )
};

export default Agendamento;